/**
 * NotificationBell Component
 * Unified NexaFlow Notification System with Socket.IO Real-Time Updates
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../hooks/useSocket';
import { notificationApi } from '../services/api';
import '../styles/notifications.css';

const NotificationBell = () => {
  const { isConnected, subscribeToNotifications, onNotification } = useSocket();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Fetch initial notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setLoading(true);
        const response = await notificationApi.getAll({ limit: 15 });
        const notifs = response.notifications || response.data || (Array.isArray(response) ? response : []);
        setNotifications(notifs);
        
        // Count unread
        const unread = typeof response.unreadCount === 'number'
          ? response.unreadCount
          : notifs.filter(n => !n.read && !n.isRead).length;
        setUnreadCount(unread);
      } catch (error) {
        console.error('Failed to fetch client notifications:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // Subscribe to real-time notifications
  useEffect(() => {
    if (isConnected) {
      subscribeToNotifications();

      onNotification((notification) => {
        setNotifications(prev => [notification, ...prev.slice(0, 14)]);
        setUnreadCount(prev => prev + 1);
      });
    }
  }, [isConnected, subscribeToNotifications, onNotification]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Mark notification as read
  const handleNotificationRead = async (notificationId) => {
    try {
      await notificationApi.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n._id === notificationId ? { ...n, read: true, isRead: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      await notificationApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  // Delete notification
  const handleDeleteNotification = async (notificationId) => {
    try {
      await notificationApi.delete(notificationId);
      const target = notifications.find(n => n._id === notificationId);
      setNotifications(prev => prev.filter(n => n._id !== notificationId));
      if (!target?.read && !target?.isRead) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return 'Recently';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const cleanTitle = (rawTitle = '') => {
    if (!rawTitle) return 'Notification';
    return rawTitle.replace(/^[\p{Emoji}\u2000-\u3300\s]+/gu, '').trim() || rawTitle;
  };

  const getCategoryMeta = (title = '', message = '', type = '') => {
    const text = `${title} ${message} ${type}`.toLowerCase();
    if (text.includes('booking') || text.includes('appointment') || text.includes('session')) {
      return {
        category: 'booking',
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        )
      };
    }
    if (text.includes('message') || text.includes('chat') || text.includes('therapist')) {
      return {
        category: 'message',
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        )
      };
    }
    if (text.includes('worksheet') || text.includes('form') || text.includes('exercise')) {
      return {
        category: 'worksheet',
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
          </svg>
        )
      };
    }
    if (text.includes('payment') || text.includes('receipt') || text.includes('refund') || text.includes('rupee')) {
      return {
        category: 'payment',
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
            <line x1="1" y1="10" x2="23" y2="10" />
          </svg>
        )
      };
    }
    return {
      category: 'default',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      )
    };
  };

  const filteredNotifications = notifications.filter(n => {
    const isUnread = !n.read && !n.isRead;
    if (activeFilter === 'unread') return isUnread;
    return true;
  });

  const handleCardClick = (notification) => {
    const isUnread = !notification.read && !notification.isRead;
    if (isUnread) {
      handleNotificationRead(notification._id);
    }
    const text = `${notification.title} ${notification.message}`.toLowerCase();
    if (text.includes('booking') || text.includes('session') || text.includes('appointment')) {
      navigate('/client/bookings');
      setShowDropdown(false);
    } else if (text.includes('worksheet')) {
      navigate('/client-dashboard');
      setShowDropdown(false);
    }
  };

  return (
    <div className="nexaflow-notif-wrapper" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        type="button"
        className={`nexaflow-bell-trigger ${showDropdown ? 'active' : ''}`}
        onClick={() => setShowDropdown(!showDropdown)}
        title="Notifications"
        aria-label="Open notifications"
      >
        <svg
          className="nexaflow-bell-svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>

        {unreadCount > 0 ? (
          <span className="nexaflow-badge-pill">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        ) : null}
      </button>

      {/* Dropdown */}
      {showDropdown && (
        <div className="nexaflow-dropdown-panel">
          {/* Header */}
          <div className="nexaflow-notif-header">
            <div className="nexaflow-header-left">
              <div className="nexaflow-header-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
              </div>
              <h3 className="nexaflow-header-title">Notifications</h3>
              {unreadCount > 0 && (
                <span className="nexaflow-count-chip">{unreadCount} New</span>
              )}
            </div>

            <div className="nexaflow-header-actions">
              {unreadCount > 0 && (
                <button
                  type="button"
                  className="nexaflow-mark-read-btn"
                  onClick={handleMarkAllAsRead}
                  title="Mark all as read"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>Mark all read</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowDropdown(false)}
                className="nexaflow-header-close-btn"
                title="Close"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="nexaflow-filter-bar">
            <button
              type="button"
              className={`nexaflow-filter-tab ${activeFilter === 'all' ? 'active' : ''}`}
              onClick={() => setActiveFilter('all')}
            >
              All
              <span className="nexaflow-tab-pill">{notifications.length}</span>
            </button>
            <button
              type="button"
              className={`nexaflow-filter-tab ${activeFilter === 'unread' ? 'active' : ''}`}
              onClick={() => setActiveFilter('unread')}
            >
              Unread
              <span className="nexaflow-tab-pill">{unreadCount}</span>
            </button>
          </div>

          {/* Notifications List */}
          <div className="nexaflow-notif-list">
            {loading ? (
              <div className="nexaflow-loading-box">
                <div className="nexaflow-spinner"></div>
                <p style={{ margin: 0, fontSize: '13px' }}>Loading notifications...</p>
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="nexaflow-empty-box">
                <div className="nexaflow-empty-icon-wrap">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                </div>
                <h4 className="nexaflow-empty-title">All caught up!</h4>
                <p className="nexaflow-empty-desc">
                  {activeFilter === 'unread'
                    ? "You have read all your notifications."
                    : "No new notifications right now. Alerts for upcoming sessions and clinical updates will appear here."}
                </p>
              </div>
            ) : (
              filteredNotifications.map(notification => {
                const isUnread = !notification.read && !notification.isRead;
                const { category, icon } = getCategoryMeta(notification.title, notification.message, notification.type);
                const title = cleanTitle(notification.title);

                return (
                  <div
                    key={notification._id}
                    className={`nexaflow-notif-card ${isUnread ? 'unread' : 'read'}`}
                    onClick={() => handleCardClick(notification)}
                  >
                    {/* Category Icon Badge */}
                    <div className={`nexaflow-cat-avatar ${category}`}>
                      {icon}
                    </div>

                    {/* Card Content */}
                    <div className="nexaflow-card-body">
                      <div className="nexaflow-card-row1">
                        <h4 className="nexaflow-card-title">{title}</h4>
                        <span className="nexaflow-card-time">
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12 6 12 12 16 14" />
                          </svg>
                          {formatRelativeTime(notification.createdAt)}
                        </span>
                      </div>

                      <p className="nexaflow-card-msg">{notification.message}</p>

                      {/* Metadata Chips */}
                      {(notification.therapistName || notification.clientName || notification.sessionDate) && (
                        <div className="nexaflow-chips-row">
                          {(notification.therapistName || notification.clientName) && (
                            <span className="nexaflow-meta-chip client">
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                <circle cx="12" cy="7" r="4" />
                              </svg>
                              {notification.therapistName || notification.clientName}
                            </span>
                          )}
                          {notification.sessionDate && (
                            <span className="nexaflow-meta-chip">
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                              </svg>
                              {new Date(notification.sessionDate).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Quick Action buttons */}
                    <div className="nexaflow-card-actions">
                      {isUnread && (
                        <button
                          type="button"
                          className="nexaflow-action-btn read"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNotificationRead(notification._id);
                          }}
                          title="Mark as read"
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </button>
                      )}
                      <button
                        type="button"
                        className="nexaflow-action-btn delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteNotification(notification._id);
                        }}
                        title="Delete"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="nexaflow-notif-footer">
            <div className="nexaflow-live-sync-pill">
              <span className="nexaflow-live-dot"></span>
              <span>Real-time Socket Connected</span>
            </div>
            <button
              type="button"
              className="nexaflow-footer-cta"
              onClick={() => {
                navigate('/client/bookings');
                setShowDropdown(false);
              }}
            >
              My Sessions →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
